import { Component, effect, inject, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthService,
  EncryptedContactIdRouteInterceptor,
  LogInWithPasswordlessLinkStrategy,
  PasswordlessLoginLinkIsExpiredError,
  PasswordlessLoginLinkIsNotValidError,
  PasswordlessLoginNetworkError,
  PostAuthRedirectGuard,
} from '@aiwyn/auth';
import { AssetUriService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { canShowValidationErrors, EMAIL_REGEX, Loadable, LoadableStatus, Logger, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { BehaviorSubject, EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE } from '../../../routing-constants';
import { DecryptApiService } from '@aiwyn/api';
import { ModalAlertComponent } from '../../main/modals/alert/modal-alert.component';
import { MatDialog } from '@angular/material/dialog';

interface PasswordlessLinkQueryParams {
  emailAddress: string;
  redirectUrl: string;
}

/**
 * https://dev.aiwyn-dev.app
 * /client-portal
 * /login
 * /passwordless?
 *  emailAddress=mOALk4iykrrCmz8yh5UbVmh7mPLpwnh_DVVuwDt2hBA=
 *  &apiKey=AIzaSyBpUJsb3K5mpw455i1a-6rZRg9MTXhWVtg
 *  &oobCode=v8BaybNtkGs36Fi44G5qxNWszX8Tw8HIohUlL4JXxgQAAAGWNmEdPw
 *  &mode=signIn
 *  &lang=en
 *  &tenantId=qaqa-3oadz
 */

@Component({
  selector: 'app-login-passwordless',
  templateUrl: './login-passwordless.component.html',
  styleUrls: ['./login-passwordless.component.scss'],
  standalone: false,
})
export class LoginPasswordlessComponent {
  readonly canShowValidationErrors = canShowValidationErrors;
  LoadableStatus = LoadableStatus;
  private launchDarklyService = inject(LaunchDarklyService);

  passwordlessEmail: WritableSignal<Loadable<string>> = signal({ status: LoadableStatus.LOADING })

  emailControl = new FormControl<string>('', [Validators.required, Validators.email]);
  emailSignInForm = new UntypedFormGroup({ email: this.emailControl });
  isSubmissionProcessing$ = new BehaviorSubject<boolean>(false);

  disablePasswordlessEmailDecryption = this.launchDarklyService.getFeatureFlagSignal(
    LaunchDarklyFlag.DISABLE_PASSWORDLESS_EMAIL_DECRYPTION
  );

  constructor(
    private readonly authService: AuthService,
    public assetUriService: AssetUriService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly logger: Logger,
    private readonly decryptApiService: DecryptApiService,
    private readonly snackbarService: SnackbarService,
    private readonly encryptedContactIdGuard: EncryptedContactIdRouteInterceptor,
    readonly passwordlessLoginStrategy: LogInWithPasswordlessLinkStrategy,
    private readonly postAuthRedirectGuard: PostAuthRedirectGuard,
    private readonly dialog: MatDialog,
  ) {
    // Prepopulate the email field and auto-submit if possible
    effect((onCleanup) => {
      let initSubscription: Subscription;

      // If the feature flag is off, do not perform any decryption and instead utilize the email stored in local storage.
      if (this.disablePasswordlessEmailDecryption()) {
        initSubscription = this.authService.getAuthEmail().pipe(
          take(1),
          map(email => {
            if (email && EMAIL_REGEX.test(email)) {
              return email;
            }

            this.logger.debug('AUTH : LoginPasswordlessComponent : initialize$ : No email address found locally. Cannot automatically sign in.');
            return undefined;
          }),
          tap(data => this.passwordlessEmail.set({ status: LoadableStatus.LOADED, data })),
          // Validate the decrypted email against the encrypted contact id's email
          switchMap(email => this.encryptedContactIdGuard.clearDecryptedContactDoesNotMatch(email).pipe(
            map(_ => email),
          )),
        ).subscribe();
      } else {
        // Default passwordless login behaviour to decrypt the email from the URL.
        initSubscription = (this.route.queryParams as Observable<PasswordlessLinkQueryParams>).pipe(
          take(1),
          withLatestFrom(this.authService.getAuthEmail()),
          switchMap(([{ emailAddress }, authEmail]) => {
            // If there is no email, see if we have a backup stored
            if (!emailAddress) {
              if (authEmail && EMAIL_REGEX.test(authEmail)) {
                this.logger.debug('AUTH : LoginPasswordlessComponent : initialize$ : Email address not present, using AUTH EMAIL.');
                this.passwordlessEmail.set({ status: LoadableStatus.LOADED, data: authEmail });
                return of(authEmail);
              }

              this.logger.debug('AUTH : LoginPasswordlessComponent : initialize$ : No email address provided. Cannot automatically sign in.');
              this.passwordlessEmail.set({ status: LoadableStatus.LOADED });
              return EMPTY;
            }

            // Decrypt the email address
            return this.decryptApiService.decryptString(emailAddress).pipe(
              tap(email => {
                // If there is no email, or it is in the wrong format, throw an error
                if (!email || !EMAIL_REGEX.test(email)) {
                  throw new Error(`Email address is ${email ? 'invalid' : 'missing'}. Cannot sign in.`);
                }
              }),
              // Validate the decrypted email against the encrypted contact id's email
              switchMap(email => this.encryptedContactIdGuard.clearDecryptedContactDoesNotMatch(email).pipe(
                map(_ => email),
              )),
              catchError(error => {
                this.logger.warn('AUTH :: LoginPasswordlessComponent :: initialize$ :: Failed to clear contactId', error);
                this.snackbarService.open(
                  'There was a problem retrieving your email address. Please enter it and continue.',
                  SnackbarType.WARNING,
                );
                this.passwordlessEmail.set({ status: LoadableStatus.ERROR });
                throw error;
              }),
              take(1),
            );
          }),
          tap(email => this.passwordlessEmail.set({ status: LoadableStatus.LOADED, data: email })),
        ).subscribe();
      }

      onCleanup(() => initSubscription?.unsubscribe());
    });

    effect(() => {
      // If we failed to load the email, enable the control for user input
      if (this.passwordlessEmail().status === LoadableStatus.ERROR) {
        this.logger.debug('AUTH : LoginPasswordlessComponent : ngOnInit : Email load failed, prompting user for email.');
        this.emailControl.enable();
      } else if (this.passwordlessEmail().status === LoadableStatus.LOADED) {
        // If we successfully loaded the email, populate and disable the control
        this.emailControl.setValue(this.passwordlessEmail().data);
        this.emailControl.disable({ emitEvent: true });

        // If not a bot, automatically submit
        if (this.passwordlessLoginStrategy.passwordlessLoginStarted) {
          this.submit();
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate([ROUTE_TOKEN_LOGIN]);
  }

  submit(): void {
    this.logger.debug('AUTH : LoginPasswordlessComponent : submit : Starting passwordless login.');

    if (this.emailSignInForm.invalid) {
      this.logger.debug('AUTH : LoginPasswordlessComponent : submit : Form is invalid. Aborting.');
      return;
    }

    if (this.isSubmissionProcessing$.value) {
      this.logger.debug('AUTH : LoginPasswordlessComponent : submit : log in attempt already processing, aborting.');
      return;
    }

    this.isSubmissionProcessing$.next(true);
    const email = this.emailControl.value;
    const url = window.location.href;

    this.authService.logIn(this.passwordlessLoginStrategy, { email, link: url }).pipe(
      take(1),
      tap(_ => this.authService.isReadyToAuthenticate.set(true)),
      withLatestFrom((this.route.queryParams as Observable<PasswordlessLinkQueryParams>)),
      // If no errors are thrown, then navigate to the main base route
      tap(([_, { redirectUrl }]) => {
        if (redirectUrl) {
          this.logger.info('AUTH : LoginPasswordlessComponent : submit : Redirect url set to ', redirectUrl);
          this.postAuthRedirectGuard.setRedirectUrl(redirectUrl);
        }
      }),
      switchMap(_ => this.router.navigate([ROUTE_TOKEN_MAIN_BASE])),
      catchError(error => {
        this.logger.warn('AUTH : LoginPasswordlessComponent : submit : Failed to log in with passwordless link.', error);

        // Keep this above the clearing of the auth email. The expired link component uses it and clears it after retrieval.
        if (error instanceof PasswordlessLoginLinkIsExpiredError) {
          // Dedicated page for expired links
          return this.router.navigate(
            [ROUTE_TOKEN_LOGIN, 'passwordless', 'expired'],
            { queryParams: { loginError: error instanceof PasswordlessLoginNetworkError ? true : undefined } }
          );
        }

        this.logger.info('AUTH : LoginPasswordlessComponent : submit : removing auth email from local store.');
        // This helps to prevent feedback loops. Plz no touchy.
        this.authService.setAuthEmail(undefined);

        if (error instanceof PasswordlessLoginLinkIsNotValidError) {
          // Login link was not valid, show the error message and redirect to the login page
          return this.dialog.open(
            ModalAlertComponent,
            {
              data: {
                title: 'Login Error',
                message: 'Login link was not valid. Please check your email and try again.',
              }
            }
          ).afterClosed().pipe(
            switchMap(_ => this.router.navigate([ROUTE_TOKEN_LOGIN])),
          );
        }

        // Show generic error message.
        return this.authService.showAuthFailureDialog(true);
      }),
      take(1),
      finalize(() => this.isSubmissionProcessing$.next(false)),
    ).subscribe();
  }
}
